import { api } from '../services/api'

/**
 * Clientes (receptores de documentos) habilitados en el portal.
 *
 * El primer segmento del path del sitio identifica al cliente: `/{clientKey}/...`.
 * Ese segmento se mapea al `accId` (workspace) que viaja en el header
 * `x-ax-workspace` de cada request al backend.
 *
 * El mapeo YA NO está hardcodeado por build: se carga desde Keycloak al arrancar la app
 * (`GET /custom-resources/websup-organizations`, público) — las organizaciones con el
 * atributo `websup`, cuyo valor es el clientKey. Habilitar un cliente nuevo = ponerle el
 * atributo a la organización, sin tocar código ni redesplegar.
 *
 * El nombre a mostrar tampoco vive aquí: se resuelve en runtime desde Keycloak via
 * `GET /custom-resources/organizations/{acc_id}` (ver el store de sesión).
 */
export interface ClientConfig {
  /** Alias de la organización cliente (acc_id / x-ax-workspace). */
  accId: string
}

type ClientMap = Record<string, ClientConfig>

let CLIENTS: ClientMap = {}

/**
 * Carga el mapeo desde Keycloak. Se llama UNA vez antes de montar la app (main.ts); después
 * la resolución es síncrona en memoria. Si falla, el mapeo queda vacío y toda ruta cae en
 * "Sitio no encontrado" (el portal no puede operar sin conocer a sus clientes).
 */
export async function loadClients(): Promise<void> {
  const realm = import.meta.env.VITE_OIDC_REALM
  const data = await api.getPublic<Record<string, { acc_id: string }>>(
    `/login/realms/${realm}/custom-resources/websup-organizations`,
  )
  CLIENTS = Object.fromEntries(
    Object.entries(data).map(([clientKey, value]) => [clientKey, { accId: value.acc_id }]),
  )
}

export function resolveClient(clientKey: string): ClientConfig | null {
  return CLIENTS[clientKey] ?? null
}

/** Búsqueda inversa: el clientKey (ruta) a partir del accId. Para el aterrizaje de invitaciones. */
export function resolveClientKeyByAccId(accId: string): string | null {
  const entry = Object.entries(CLIENTS).find(([, config]) => config.accId === accId)
  return entry ? entry[0] : null
}
